import { formatDateDisplay } from '../utils/dateDisplay';

/**
 * Employee Data Schemas and Validation
 */

// Employee validation schema
export const employeeSchema = {
  empcode: {
    required: true,
    minLength: 3,
    maxLength: 10,
    pattern: /^[A-Z0-9]+$/,
    message: 'Employee code must be 3-10 characters, alphanumeric only'
  },
  first_name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]+$/,
    message: 'First name must be 2-50 characters, letters only'
  },
  last_name: {
    required: false,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]*$/,
    message: 'Last name must be letters only'
  },
  email_address: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  phone_no: {
    required: true,
    pattern: /^[+]?[\d\s-()]+$/,
    minLength: 10,
    maxLength: 15,
    message: 'Please enter a valid phone number'
  },
  date_of_joining: {
    required: true,
    type: 'date',
    validate: (value) => new Date(value) <= new Date(),
    message: 'Joining date cannot be in the future'
  },
  address: {
    required: false,
    maxLength: 500,
    message: 'Address must be less than 500 characters'
  },
  role: {
    required: true,
    enum: ['employee', 'admin', 'manager', 'HR', 'accountant'],
    message: 'Please select a valid role'
  },
  designation: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: 'Designation must be 2-100 characters'
  }
};

// Profile update schema (subset of employee schema)
export const profileUpdateSchema = {
  first_name: employeeSchema.first_name,
  last_name: employeeSchema.last_name,
  phone_no: employeeSchema.phone_no,
  address: employeeSchema.address
};

// Employee search filters schema
export const employeeFiltersSchema = {
  search: {
    required: false,
    minLength: 2,
    message: 'Search term must be at least 2 characters'
  },
  department: {
    required: false,
    enum: ['Engineering', 'HR', 'Finance', 'Marketing', 'Sales', 'Operations'],
    message: 'Please select a valid department'
  },
  role: {
    required: false,
    enum: ['employee', 'admin', 'manager', 'HR', 'accountant'],
    message: 'Please select a valid role'
  },
  status: {
    required: false,
    enum: ['active', 'inactive', 'on-leave'],
    message: 'Please select a valid status'
  }
};

// Employee data types
export const EmployeeTypes = {
  ROLES: ['employee', 'admin', 'manager', 'HR', 'team_lead', 'accountant'],
  DEPARTMENTS: ['Engineering', 'HR', 'Finance', 'Marketing', 'Sales', 'Operations'],
  STATUS: ['active', 'inactive', 'on-leave'],
  DESIGNATIONS: [
    'Devops Engineer',
    'Software Developer Engineer',
    'DevOps Intern',
    'Software Developer Intern',
    'Designer',
    'HR',
    'Accountant',
    'Senior Accountant',
    'Finance Executive',
    'Business Development Intern',
    'Business Development Executive'
  ]
};

// Default employee data
export const defaultEmployeeData = {
  empcode: '',
  first_name: '',
  last_name: '',
  email_address: '',
  phone_no: '',
  date_of_joining: '',
  address: '',
  role: 'employee',
  designation: '',
  department: '',
  status: 'active'
};

// Employee table columns configuration
export const employeeTableColumns = [
  {
    key: 'empcode',
    label: 'Emp Code',
    sortable: true,
    searchable: true
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    searchable: true,
    render: (employee) => `${employee.first_name} ${employee.last_name || ''}`.trim()
  },
  {
    key: 'email_address',
    label: 'Email',
    sortable: true,
    searchable: true
  },
  {
    key: 'department',
    label: 'Department',
    sortable: true,
    filterable: true
  },
  {
    key: 'role',
    label: 'Role',
    sortable: true,
    filterable: true
  },
  {
    key: 'date_of_joining',
    label: 'Join Date',
    sortable: true,
    render: (employee) => formatDateDisplay(employee.date_of_joining)
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    filterable: true
  }
];

export default {
  employeeSchema,
  profileUpdateSchema,
  employeeFiltersSchema,
  EmployeeTypes,
  defaultEmployeeData,
  employeeTableColumns
};
